/**
 * DOM UI layer for DLSM Hoops. Owns every screen the player sees over the
 * canvas: start, in-play HUD, miss toasts, and the win/loss result +
 * email-claim panel. Builds its own DOM tree on construction and mounts it
 * to document.body — index.html stays a bare shell.
 *
 * Contract: artifacts/interfaces.md
 */

export interface GameUiCallbacks {
  onStart(): void;
  onRestart(): void;
  /** `win` claims the trade credit; `card` sends the consolation scorecard. */
  onClaim(email: string, makes: number, kind: 'win' | 'card'): Promise<void>;
}

export interface HudState {
  makes: number;
  target: number;
  secondsLeft: number;
  streak: number;
  fire: boolean;
}

type MissReason = 'rim' | 'backboard' | 'short' | 'long' | 'wide';

const MISS_REASON_TEXT: Record<MissReason, string> = {
  rim: 'So close — off the rim',
  backboard: 'Off the backboard',
  short: 'Short',
  long: 'Long',
  wide: 'Wide',
};

const CLAIM_NOTE_TEXT =
  'A DLSM specialist will email you to apply the trade credit to your account.';

const CARD_NOTE_TEXT = 'Send the card to the DLSM Team.';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** WhatsApp deep link to the DLSM Team. The reference is added to the prefilled
 *  message once a run has one, so the player does not have to type it. */
const TEAM_WHATSAPP = 'https://wa.me/61422592154';

const STREAK_PIP_COUNT = 3;

const MISS_TOAST_VISIBLE_MS = 900;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export class GameUi {
  private readonly callbacks: GameUiCallbacks;
  private readonly root: HTMLDivElement;

  // Start screen
  private readonly startScreen: HTMLDivElement;
  private readonly playButton: HTMLButtonElement;

  // HUD
  private readonly hud: HTMLDivElement;
  private readonly makesValue: HTMLSpanElement;
  private readonly clockPill: HTMLDivElement;
  private readonly clockValue: HTMLSpanElement;
  private readonly streakPips: HTMLDivElement[];
  private readonly fireBadge: HTMLDivElement;

  // Miss toast
  private readonly missToast: HTMLDivElement;
  private missToastTimer: ReturnType<typeof setTimeout> | undefined;

  // Result screen
  private readonly resultScreen: HTMLDivElement;
  private readonly resultTitle: HTMLHeadingElement;
  private readonly resultMakes: HTMLParagraphElement;
  private readonly resultMakesStrong: HTMLElement;
  private readonly resultMakesTail: Text;
  private readonly retryButton: HTMLButtonElement;
  private readonly winBlock: HTMLDivElement;
  private readonly claimForm: HTMLFormElement;
  private readonly emailInput: HTMLInputElement;
  private readonly claimButton: HTMLButtonElement;
  private readonly claimStatus: HTMLParagraphElement;
  private readonly honeypot: HTMLInputElement;
  private readonly claimNote: HTMLParagraphElement;
  private teamLink!: HTMLAnchorElement;
  private readonly lossBlock: HTMLDivElement;
  private lastMakes = 0;
  private mode: 'win' | 'card' = 'win';
  private readonly claimSuccess: HTMLDivElement;
  private readonly successTitle: HTMLParagraphElement;
  private readonly successBody: HTMLParagraphElement;

  private lastHud: HudState | null = null;
  private claimPending = false;
  private disposed = false;

  constructor(callbacks: GameUiCallbacks) {
    this.callbacks = callbacks;

    this.root = el('div', 'ui-root');

    // ---------------- Start screen ----------------
    this.startScreen = el('div', 'screen screen-start');
    const lockup = el('div', 'brand-lockup');
    const logo = el('img', 'brand-logo');
    logo.src = '/dlsm-logo.png';
    logo.alt = '';
    logo.decoding = 'async';
    const kicker = el('span', 'brand-kicker', 'DLSM Hoops');
    lockup.append(logo, kicker);
    const title = el('h1', 'screen-title');
    title.innerHTML = 'Sink <span class="accent">5</span> in 60 seconds';
    const rulesLine = el(
      'p',
      'screen-line',
      'Swipe up to shoot. Longer swipe, longer shot.',
    );
    const prizeLine = el('p', 'screen-line');
    prizeLine.innerHTML = 'Win up to <strong>$1,000 trade credit</strong>.';
    this.playButton = el('button', 'btn-primary', 'Play');
    this.playButton.type = 'button';
    this.startScreen.append(lockup, title, rulesLine, prizeLine, this.playButton);
    this.playButton.addEventListener('click', this.handlePlayClick);

    // ---------------- HUD ----------------
    this.hud = el('div', 'hud');
    this.hud.hidden = true;
    this.hud.setAttribute('aria-live', 'polite');

    const leftCluster = el('div', 'hud-cluster');
    const makesPill = el('div', 'hud-pill');
    makesPill.append(el('span', 'hud-pill-label', 'Score'));
    this.makesValue = el('span', 'hud-pill-value', '0 / 5');
    makesPill.append(this.makesValue);

    this.clockPill = el('div', 'hud-pill hud-pill-clock');
    this.clockPill.append(el('span', 'hud-pill-label', 'Clock'));
    this.clockValue = el('span', 'hud-pill-value', '60');
    this.clockPill.append(this.clockValue);

    leftCluster.append(makesPill, this.clockPill);

    const rightCluster = el('div', 'hud-cluster');
    const streakRow = el('div', 'hud-streak');
    streakRow.setAttribute('aria-hidden', 'true');
    this.streakPips = [];
    for (let i = 0; i < STREAK_PIP_COUNT; i += 1) {
      const pip = el('div', 'streak-pip');
      this.streakPips.push(pip);
      streakRow.append(pip);
    }
    this.fireBadge = el('div', 'hud-fire', 'Match point');
    this.fireBadge.hidden = true;
    rightCluster.append(streakRow, this.fireBadge);

    this.hud.append(leftCluster, rightCluster);

    // ---------------- Miss toast ----------------
    this.missToast = el('div', 'miss-toast');
    this.missToast.setAttribute('role', 'status');
    this.missToast.setAttribute('aria-live', 'polite');

    // ---------------- Result screen ----------------
    this.resultScreen = el('div', 'screen screen-result');
    this.resultScreen.hidden = true;
    const panel = el('div', 'panel');

    this.resultTitle = el('h2', 'screen-title', '');
    this.resultMakes = el('p', 'panel-makes');
    this.resultMakesStrong = el('strong', undefined, '0');
    this.resultMakesTail = document.createTextNode(' of 5.');
    this.resultMakes.append('You sank ', this.resultMakesStrong, this.resultMakesTail);

    // Loss content
    this.retryButton = el('button', 'btn-secondary', 'Retry');
    this.retryButton.type = 'button';
    this.retryButton.addEventListener('click', this.handleRetryClick);

    // Win content
    this.winBlock = el('div', 'panel-win');
    const winPrizeLine = el('p', 'screen-line');
    winPrizeLine.innerHTML = 'Your <strong>$1,000 trade credit</strong> is one step away.';

    this.claimForm = el('form', 'claim-form');
    // The label is kept for screen readers but not shown: the field says what it wants.
    const label = el('label', 'claim-label visually-hidden', 'Your DLSM account email');
    label.htmlFor = 'claim-email';
    this.emailInput = el('input', 'claim-input');
    this.emailInput.type = 'email';
    this.emailInput.id = 'claim-email';
    this.emailInput.name = 'email';
    this.emailInput.placeholder = 'Your DLSM account email';
    this.emailInput.autocomplete = 'email';
    this.emailInput.required = true;
    this.emailInput.inputMode = 'email';
    this.emailInput.addEventListener('blur', () => {
      this.emailInput.setAttribute('data-touched', 'true');
    });

    this.claimButton = el('button', 'btn-primary', 'Claim trade credit');
    this.claimButton.type = 'submit';

    this.claimStatus = el('p', 'claim-status');
    this.claimStatus.hidden = true;
    this.claimStatus.setAttribute('role', 'alert');

    this.claimNote = el('p', 'claim-note', CLAIM_NOTE_TEXT);

    // Kept as a field so the form can be reset with everything else.
    const honeypot = el('input', 'claim-honeypot');
    honeypot.type = 'text';
    honeypot.name = 'company';
    honeypot.tabIndex = -1;
    honeypot.autocomplete = 'off';
    honeypot.setAttribute('aria-hidden', 'true');
    this.honeypot = honeypot;
    this.honeypot.value = '';

    this.claimForm.append(label, this.emailInput, honeypot, this.claimButton, this.claimStatus);
    this.claimForm.addEventListener('submit', this.handleClaimSubmit);

    this.claimSuccess = el('div', 'claim-success');
    this.claimSuccess.hidden = true;
    this.claimNote.hidden = false;
    this.successTitle = el('p', 'claim-success-title', 'Claim received');
    this.successBody = el(
      'p',
      'screen-line',
      'A DLSM specialist will confirm it against your account and apply the credit.',
    );
    this.claimSuccess.append(this.successTitle, this.successBody);

    this.winBlock.append(winPrizeLine);
    this.winBlock.hidden = true;

    this.lossBlock = el('div', 'panel-loss');
    const lossLine = el('p', 'screen-line');
    const cxLink = el('a', 'cx-link', 'DLSM Team');
    this.teamLink = cxLink;
    cxLink.href = TEAM_WHATSAPP;
    cxLink.target = '_blank';
    cxLink.rel = 'noopener noreferrer';
    lossLine.append(
      'Short of five, but not going away empty handed. Download your card and send it to ',
      cxLink,
      ' to have your trade credit applied.',
    );
    this.lossBlock.append(lossLine);
    this.lossBlock.hidden = true;

    panel.append(
      this.resultTitle,
      this.resultMakes,
      this.winBlock,
      this.lossBlock,
      this.claimForm,
      this.claimSuccess,
      this.claimNote,
      this.retryButton,
    );
    this.resultScreen.append(panel);

    this.root.append(this.startScreen, this.hud, this.missToast, this.resultScreen);
    document.body.append(this.root);
  }

  setHud(state: HudState): void {
    if (this.hud.hidden) {
      this.hud.hidden = false;
    }

    const prev = this.lastHud;

    if (!prev || prev.makes !== state.makes || prev.target !== state.target) {
      this.makesValue.textContent = `${state.makes} / ${state.target}`;
    }

    if (!prev || prev.secondsLeft !== state.secondsLeft) {
      const seconds = Math.max(0, Math.ceil(state.secondsLeft));
      this.clockValue.textContent = String(seconds);
      this.clockPill.classList.toggle('is-urgent', seconds <= 10);
    }

    if (!prev || prev.streak !== state.streak) {
      const filled = Math.min(state.streak, STREAK_PIP_COUNT);
      this.streakPips.forEach((pip, i) => {
        pip.classList.toggle('is-filled', i < filled);
      });
    }

    if (!prev || prev.fire !== state.fire) {
      this.fireBadge.hidden = !state.fire;
    }

    this.lastHud = state;
  }

  showMissReason(reason: MissReason): void {
    this.missToast.textContent = MISS_REASON_TEXT[reason];
    this.missToast.classList.remove('is-visible');
    // Force reflow so retriggering the same reason still restarts the transition.
    void this.missToast.offsetWidth;
    this.missToast.classList.add('is-visible');

    if (this.missToastTimer !== undefined) {
      clearTimeout(this.missToastTimer);
    }
    this.missToastTimer = setTimeout(() => {
      this.missToast.classList.remove('is-visible');
      this.missToastTimer = undefined;
    }, MISS_TOAST_VISIBLE_MS);
  }

  showResult(won: boolean, makes: number): void {
    this.hud.hidden = true;
    this.startScreen.hidden = true;

    this.resultMakesStrong.textContent = String(makes);
    // A streak pays double, so a winning run can finish past the target. "6 of 5"
    // reads like a bug to the person who just did it.
    this.resultMakesTail.textContent = makes > 5 ? '.' : ' of 5.';
    this.resultTitle.textContent = won ? 'Nailed it' : "Time's up";

    this.retryButton.hidden = won;
    this.winBlock.hidden = !won;
    this.lossBlock.hidden = won;
    this.lastMakes = makes;
    this.mode = won ? 'win' : 'card';
    this.claimForm.hidden = false;
    this.claimButton.textContent = won ? 'Claim trade credit' : 'Get my card';
    this.claimNote.textContent = won ? CLAIM_NOTE_TEXT : CARD_NOTE_TEXT;
    this.claimNote.hidden = false;
    this.claimSuccess.hidden = true;


    if (won) {
      this.resetClaimForm();
    }

    this.resultScreen.hidden = false;
  }

  /** Once a card has a reference, the WhatsApp link carries it in the message. */
  setCardReference(code: string): void {
    const text = encodeURIComponent(`Hi DLSM Team, here is my DLSM Hoops card. Reference ${code}.`);
    this.teamLink.href = `${TEAM_WHATSAPP}?text=${text}`;
  }

  showClaimSuccess(): void {
    this.claimPending = false;
    this.claimForm.hidden = true;
    this.claimStatus.hidden = true;
    this.claimSuccess.hidden = false;
    this.claimForm.hidden = true;
    this.successTitle.textContent = this.mode === 'win' ? 'Claim received' : 'Card saved';
    this.successBody.textContent =
      this.mode === 'win'
        ? 'A DLSM specialist will confirm it against your account and apply the credit.'
        : 'Send it to the DLSM Team on WhatsApp and they will apply your trade credit.';
    // The note explains what happens next; once it HAS happened the success line
    // says the same thing, so only one of them is ever on screen.
    this.claimNote.hidden = true;
  }

  showClaimError(message: string): void {
    this.claimPending = false;
    this.claimButton.disabled = false;
    this.emailInput.disabled = false;
    this.claimButton.textContent = this.mode === 'win' ? 'Claim trade credit' : 'Get my card';
    this.claimStatus.textContent = message;
    this.claimStatus.hidden = false;
    this.claimStatus.classList.remove('is-success');
    this.claimStatus.classList.add('is-error');
  }

  /** Returns the UI to the in-play state: hides start/result, shows the HUD,
   * clears the miss toast. Used both to enter the first run and for the
   * one-tap retry loop. */
  reset(): void {
    this.startScreen.hidden = true;
    this.resultScreen.hidden = true;
    this.resetClaimForm();

    if (this.missToastTimer !== undefined) {
      clearTimeout(this.missToastTimer);
      this.missToastTimer = undefined;
    }
    this.missToast.classList.remove('is-visible');

    this.streakPips.forEach((pip) => pip.classList.remove('is-filled'));
    this.fireBadge.hidden = true;
    this.clockPill.classList.remove('is-urgent');
    this.lastHud = null;

    this.hud.hidden = false;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    if (this.missToastTimer !== undefined) {
      clearTimeout(this.missToastTimer);
      this.missToastTimer = undefined;
    }

    this.playButton.removeEventListener('click', this.handlePlayClick);
    this.retryButton.removeEventListener('click', this.handleRetryClick);
    this.claimForm.removeEventListener('submit', this.handleClaimSubmit);

    this.root.remove();
  }

  private readonly handlePlayClick = (): void => {
    this.startScreen.hidden = true;
    this.hud.hidden = false;
    this.callbacks.onStart();
  };

  private readonly handleRetryClick = (): void => {
    this.resultScreen.hidden = true;
    this.hud.hidden = false;
    this.lastHud = null;
    this.callbacks.onRestart();
  };

  private readonly handleClaimSubmit = (event: SubmitEvent): void => {
    event.preventDefault();
    if (this.claimPending) return;

    const email = this.emailInput.value.trim();
    this.emailInput.setAttribute('data-touched', 'true');

    if (!EMAIL_PATTERN.test(email)) {
      this.claimStatus.textContent = 'Enter a valid email address.';
      this.claimStatus.hidden = false;
      this.claimStatus.classList.remove('is-success');
      this.claimStatus.classList.add('is-error');
      this.emailInput.focus();
      return;
    }

    this.claimPending = true;
    this.claimButton.disabled = true;
    this.emailInput.disabled = true;
    this.claimButton.textContent = 'Sending…';
    this.claimStatus.hidden = true;

    this.callbacks
      .onClaim(email, this.lastMakes, this.mode)
      .then(() => {
        if (this.disposed) return;
        this.showClaimSuccess();
      })
      .catch((error: unknown) => {
        if (this.disposed) return;
        const message = error instanceof Error ? error.message : 'Could not submit. Try again.';
        this.showClaimError(message);
      });
  };

  private resetClaimForm(): void {
    this.claimPending = false;
    this.claimForm.hidden = false;
    this.claimSuccess.hidden = true;
    this.claimNote.hidden = false;
    this.claimStatus.hidden = true;
    this.claimStatus.classList.remove('is-success', 'is-error');
    this.emailInput.value = '';
    this.emailInput.disabled = false;
    this.emailInput.removeAttribute('data-touched');
    this.claimButton.disabled = false;
    this.claimButton.textContent = this.mode === 'win' ? 'Claim trade credit' : 'Get my card';
  }
}
